<!-- @format -->

<template>
  <div>
    <div class="sixteen wide column">
      <div class="ui attached message" style="padding: 20px">
        <strong style="font-size: x-large"
          >Will you meet your R{{
            formatNr(retirementAnnuity.target)
          }}
          target? </strong
        ><br />
        <span>
          using {{ retirementAnnuity.raOutputs[0].strategyDescription }}</span
        >
      </div>

      <StatCard
        style="border-left: 1px solid #d4d4d5;border-right: 1px solid #d4d4d5;"
        :goal1="{
          header: retirementAnnuity.raOutputs[0].strategyDescription,
          value: 'R' + formatNr(retirementAnnuity.medianBalancePv, '0,0.0a'),
          footer: 'average market conditions',
        }"
        :goal2="{
          header: retirementAnnuity.raOutputs[0].strategyDescription,
          value:
            'R' + formatNr(retirementAnnuity.pessimisticBalance3Pv, '0,0.0a'),
          footer: 'worse than average market conditions',
        }"
        :goal3="{
          header: retirementAnnuity.raOutputs[0].strategyDescription,
          value:
            'R' + formatNr(retirementAnnuity.optimisticBalance3Pv, '0,0.0a'),
          footer: 'better than average market',
        }"
      />

      <div class="ui attached segment" style="border-top: none">
        <p style="padding-top: 10px">
          We projected your portfolio values under various market conditions.

          <br />

          + Under "average market conditions", you will reach
          <strong
            >R{{
              formatNr(retirementAnnuity.medianBalancePv, "0,0.0a")
            }}</strong
          >
          (in today's terms) at retirement;
          <strong>
            {{
              retirementAnnuity.medianBalancePv < retirementAnnuity.target
                ? " falling short by R" +
                  formatNr(
                    retirementAnnuity.target - retirementAnnuity.medianBalancePv
                  )
                : " exceeding your target by R" +
                  formatNr(
                    retirementAnnuity.medianBalancePv - retirementAnnuity.target
                  )
            }}</strong
          >. <br />

          + Under "worse than average market conditions" (ie the bottom
          {{ retirementAnnuity.pessimisticBalance3Pctl }}% of projections) you
          will reach
          <strong
            >R{{
              formatNr(retirementAnnuity.pessimisticBalance3Pv, "0,0.0a")
            }}</strong
          >
          (in today's terms) at retirement;
          <strong>
            {{
              retirementAnnuity.pessimisticBalance3Pv < retirementAnnuity.target
                ? " falling short by R" +
                  formatNr(
                    retirementAnnuity.target -
                      retirementAnnuity.pessimisticBalance3Pv
                  )
                : " exceeding your target by R" +
                  formatNr(
                    retirementAnnuity.pessimisticBalance3Pv -
                      retirementAnnuity.target
                  )
            }}</strong
          >. <br />
          + Under "better than average market conditions" (ie the top
          {{ 100 - retirementAnnuity.optimisticBalance3Pctl }}% of projections),
          you will reach
          <strong
            >R{{
              formatNr(retirementAnnuity.optimisticBalance3Pv, "0,0.0a")
            }}</strong
          >
          (in today's terms) at retirement;
          <strong>
            {{
              retirementAnnuity.optimisticBalance3Pv < retirementAnnuity.target
                ? " falling short by R" +
                  formatNr(
                    retirementAnnuity.target -
                      retirementAnnuity.optimisticBalance3Pv
                  )
                : " exceeding your target by R" +
                  formatNr(
                    retirementAnnuity.optimisticBalance3Pv -
                      retirementAnnuity.target
                  )
            }}</strong
          >. <br />
          <br />
          <small style="text-align: right;margin-right: 25px;">
            See
            <a href="#" @click.prevent="clickDisclaimer"
              >here how we calculate projected balances</a
            ></small
          >
        </p>
        <sui-modal v-model="showDisclaimer">
          <sui-modal-header
            >Projection Methodology & Assumptions</sui-modal-header
          >
          <sui-modal-content style="height: 400px">
            <Disclaimer
              :moreLikelyZScore="moreLikelyZScore"
              :lessLikelyZScore="lessLikelyZScore"
            />
          </sui-modal-content>
          <sui-modal-actions>
            <sui-button @click.native="showDisclaimer = false"
              >Close</sui-button
            >
          </sui-modal-actions>
        </sui-modal>
      </div>
      <div class="ui  segment">
        <DxChart
          style="height: 220px"
          :customize-label="customizeLabelNumeric"
          id="chart"
          :data-source="chartData"
          title=""
        >
          <DxCommonSeriesSettings argument-field="argument" type="bar">
            <DxLabel :visible="true">
              <DxFormat :precision="0" type="fixedPoint" />
            </DxLabel>
          </DxCommonSeriesSettings>
          <DxValueAxis :max-value-margin="0.01">
            <DxConstantLine
              :width="3"
              :value="retirementAnnuity.target"
              color="black"
              dash-style="dash"
            >
              <DxLabel
                :text="
                  'Your Target of R ' + this.formatNr(retirementAnnuity.target)
                "
              />
            </DxConstantLine>
          </DxValueAxis>
          <DxSeries value-field="medianValue" name="Median" color="black" />
          <DxSeries
            value-field="pessimisticValue"
            name="Worse than average market conditions"
            :color="appearanceSettings.appearanceSchema.graphColor"
          />
          <DxSeries
            value-field="optimisticValue"
            name="Better than average market conditions"
            :color="appearanceSettings.appearanceSchema.graphColor"
          />
        </DxChart>
      </div>
    </div>
  </div>
</template>

<script>
import Disclaimer from "@/components/DisclaimerV23.vue";
import numeral from "numeral";
import {
  DxChart,
  DxBullet,
  DxSeries,
  DxArgumentAxis,
  DxValueAxis,
  DxCommonAxisSettings,
  DxCommonSeriesSettings,
  DxConstantLine,
  DxGrid,
  DxMargin,
  DxLegend,
  DxTick,
  DxTooltip,
  DxLabel,
  DxFont,
} from "devextreme-vue/chart";
import { logout } from "@/gateway";
import StatCard from "@/components/StatCard.vue";
// @ is an alias to /src
import { mapState, mapActions } from "vuex";
export default {
  name: "Home",
  props: ["retirementAnnuity", "clientName", "appearanceSettings"],
  computed: {
    chartData() {
      var final = [];
      final.push({
        optimisticValue: this.retirementAnnuity.optimisticBalance3Pv,
        pessimisticValue: this.retirementAnnuity.pessimisticBalance3Pv,
        medianValue: this.retirementAnnuity.medianBalancePv,
        target: this.retirementAnnuity.target,
        argument: "Portfolio balance at retirement",
      });

      return final;
    },
  },
  data() {
    return {
      showDisclaimer: false,
    };
  },
  methods: {
    clickDisclaimer() {
      this.showDisclaimer = true;
      this.$emit("handleSelected", {
        showDisclaimer: this.showDisclaimer,
      });
    },
    formatNr(val, fmt) {
      if (fmt) return numeral(val).format(fmt);
      else return numeral(val).format("0,0.0a");
    },

    // chart points and values
    customizePoint({ value }) {
      if (value > 0) {
        return { color: "#ff7c7c", hoverStyle: { color: "#ff7c7c" } };
      } else if (value < this.lowAverage) {
        return { color: "#8c8cff", hoverStyle: { color: "#8c8cff" } };
      }
    },
    customizeLabel({ value }) {
      if (value > 0) {
        return {
          visible: true,
          backgroundColor: "",
          customizeText: function({ valueText }) {
            return `R ${valueText}`;
          },
        };
      }
    },
    customizeLabelNumeric({ value }) {
      if (value > 0) {
        var vt = numeral(value).format("0,0.0a");
        return {
          border: {
            visible: false,
          },
          verticalOffset: 50,
          visible: true,
          font: {
            size: "x-large",
          },
          customizeText: function({ valueText }) {
            console.log("valueText", valueText, value);

            return `R ${vt}`;
          },
        };
      }
    },
    customizeText({ valueText }) {
      return `${valueText}&#176F`;
    },
  },
  components: {
    Disclaimer,
    StatCard,
    DxChart,
    DxConstantLine,
    DxBullet,
    DxSeries,
    DxArgumentAxis,
    DxValueAxis,
    DxCommonAxisSettings,
    DxCommonSeriesSettings,
    DxGrid,
    DxMargin,
    DxLegend,
    DxTick,
    DxTooltip,
    DxLabel,
    DxFont,
  },
};
</script>
